import React from 'react';
import { Progress } from './progress';
import { Icon } from '../icons/Icon';

export interface ProgressIndicatorProps {
  progress?: number;
  flowName?: string;
  courseName?: string | null;
  chapterName?: string | null;
  lessonPosition?: number | null;
  lessonCount?: number | null;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ progress, flowName, courseName, chapterName, lessonPosition, lessonCount }) => {
  if (progress == null) {
    return null; // nothing gets rendered
  }

  const roundedProgress = Math.round(progress);

  if (courseName) {
    // Course lessons use a compact two-line header: the lesson title, then one
    // muted line of course, chapter and position, with progress beside them.
    const lessonLabel = lessonPosition ? `Lesson ${lessonPosition}${lessonCount ? ` of ${lessonCount}` : ''}` : '';
    const context = [courseName, chapterName, lessonLabel].filter(Boolean).join(' · ');
    return (
      <div className="flex w-full items-center gap-3 animate-fade-in sm:gap-4">
        <span className="hidden shrink-0 sm:inline-flex">
          <Icon size={14} name="logo" />
        </span>
        <div className="flow-chrome-text min-w-0 flex-1 text-foundation-base-darkbase-dark-500 md:max-w-md md:flex-none">
          <div className="truncate text-sm font-semibold leading-tight sm:text-base" title={flowName}>
            {flowName ?? "Ready when you are"}
          </div>
          <div
            className="flow-chrome-muted truncate text-xs leading-tight text-foundation-base-darkbase-dark-500/65 sm:text-[0.8125rem]"
            title={context}
            data-testid="lesson-header-context"
          >
            {context}
          </div>
        </div>
        <div className="flex w-14 shrink-0 flex-col items-end gap-1 sm:w-48 sm:flex-row sm:items-center sm:gap-2">
          <Progress value={progress} aria-label="Lesson progress" className="flow-chrome-track order-2 h-0.5 w-full sm:order-1 sm:flex-1" />
          <span className="flow-chrome-text order-1 whitespace-nowrap font-body-m-semibold text-xs text-foundation-base-darkbase-dark-500 sm:order-2 sm:text-sm">
            {roundedProgress}%
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center gap-2 animate-fade-in sm:gap-4">
      {/* Logo + text */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="hidden shrink-0 sm:inline-flex">
          <Icon size={14} name="logo" />
        </span>
        <div className="flow-chrome-text min-w-0 text-foundation-base-darkbase-dark-500">
          <div className="truncate italic text-xs sm:text-sm md:text-base">
            {flowName ?? "Ready when you are"}
          </div>
        </div>
      </div>

      {/* Percentage + bar */}
      <div className="flex w-[34vw] min-w-[5.25rem] max-w-[22.5rem] shrink-0 items-center gap-1.5 sm:w-[15rem] sm:min-w-[7.5rem] sm:gap-2">
        <div>
          <span className="flow-chrome-text whitespace-nowrap font-body-m-semibold text-xs text-foundation-base-darkbase-dark-500 sm:text-sm">
            {roundedProgress}%
          </span>
        </div>
        <Progress value={progress} aria-label="Module progress" className="flow-chrome-track flex-1 h-0.5" />
      </div>
    </div>
  );
};
